import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  Paper,
  CircularProgress,
  Box,
  Divider,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { ArrowBack, NotificationsActive, BugReport } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import APIService from "../../Api/ApiService";
import { TriggerNotificationSettingsLink } from "../../Routes";

import "./OptionsPage.scss";

const OptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allowNotifications, setAllowNotifications] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // DEV: States for the Test Push button
  const [sendingPush, setSendingPush] = useState(false);
  const [pushStatus, setPushStatus] = useState<{
    open: boolean;
    success: boolean;
    msg: string;
  }>({
    open: false,
    success: false,
    msg: "",
  });

  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        setLoading(true);
        // Assumes APIService.GetMyProfile() returns the email
        const profile = await APIService.GetMyProfile();
        setAllowNotifications(profile.allow_notifications);
        setUserEmail(profile.email); // Save email for the test push
        if (profile && profile.role === "admin") {
          setIsAdmin(true);
        }
      } catch (err) {
        console.error("Error fetching user settings", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserSettings();
  }, []);

  const handleToggleNotifications = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked;
    setUpdating(true);
    try {
      await APIService.UpdateNotificationSettings(newValue);
      setAllowNotifications(newValue);

      if (newValue) {
        navigate(TriggerNotificationSettingsLink);
      }
    } catch (err) {
      console.error("Error updating notifications", err);
    } finally {
      setUpdating(false);
    }
  };

  /**
   * DEV: Uses APIService to trigger a test push
   * Code to be removed in production
   */
  const handleSendTestPush = async () => {
    setSendingPush(true);
    try {
      // --- CHANGED HERE: Using APIService instead of raw fetch ---
      await APIService.DevSendPush(
        "Test Push 🚀",
        "This is a test notification triggered from Options.",
        { screen: "options" },
      );

      // If APIService didn't throw an error, it was successful
      setPushStatus({ open: true, success: true, msg: "Push request sent!" });
    } catch (error: any) {
      console.error("Dev Push Error:", error);
      // Extract error message if available
      const errorMsg = error.message || "Error calling Dev API.";
      setPushStatus({ open: true, success: false, msg: errorMsg });
    } finally {
      setSendingPush(false);
    }
  };

  const handleCloseSnackbar = () => {
    setPushStatus({ ...pushStatus, open: false });
  };

  if (loading) {
    return (
      <Box className="options-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="options-page-root">
      <div className="options-header">
        <IconButton onClick={() => navigate("/dashboard")} color="inherit">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" component="h1">
          Impostazioni
        </Typography>
        <div style={{ width: 48 }} />
      </div>

      <Container maxWidth="sm" className="options-container">
        <Paper elevation={0} className="options-paper">
          <div className="option-row">
            <div className="option-info">
              <div className="option-icon-wrapper">
                <NotificationsActive color="primary" />
              </div>
              <div className="option-text">
                <Typography variant="subtitle1" fontWeight="600">
                  Notifiche Push
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ricevi avvisi in tempo reale quando un servizio va in errore.
                </Typography>
              </div>
            </div>
            <FormControlLabel
              control={
                <Switch
                  checked={allowNotifications}
                  onChange={handleToggleNotifications}
                  disabled={updating}
                  color="primary"
                />
              }
              label=""
            />
          </div>

          {isAdmin && (
            <>
              <Divider />
              <Box
                p={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
                bgcolor="#f5f5f5"
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  🛠 DEV ZONE (Remove later)
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={
                    sendingPush ? <CircularProgress size={16} /> : <BugReport />
                  }
                  onClick={handleSendTestPush}
                  disabled={sendingPush || !userEmail}
                >
                  Send Test Push
                </Button>
              </Box>
            </>
          )}
        </Paper>

        <Box mt={4} textAlign="center">
          <Typography variant="caption" color="textSecondary">
            ID Utente: {localStorage.getItem("loggedUserId")}
          </Typography>
        </Box>
      </Container>

      {/* Feedback Snackbar */}
      <Snackbar
        open={pushStatus.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={pushStatus.success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {pushStatus.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OptionsPage;
